// web/src/player/profanity.ts
import type { CaptionMask, BleepMode } from "@/config/censorship";
import { DEFAULT_CAPTION_MASK, DEFAULT_BLEEP_MODE, KEEP_FIRST_LETTERS } from "@/config/censorship";

export const ALLOW_FIRST_MS: Record<string, number> = {
  fuck:0, fucked:0, fucking:0, fucker:0, fucks:0, fuckin:0, fuckwad:0, fucksake:0, "f*ck":10,
  motherfucker:10, motherfuckers:10, motherfucking:10, fuckface:0, fuckhead:0, clusterfuck:100, mindfuck:100,
  shit:50, shitshow:50, shits:50, shitting:50, shitty:50, "sh*t":50, shithole:50, bullshit:100, dipshit:100,
  bitch:50, bitches:50, "b*tch":50, ass:10, asshole:50, assholes:0,
  damn:100, damned:100, goddamn:100, dick:10, dicks:10,
  cunt:10, cunts:10, pussy:10, whore:10, whores:10, slut:10, sluts:10, faggot:10, vagina:10, cock:10, cocks:10,
  bastard:50, bastards:50, piss:50, pissed:50, pissing:50, hell:100, crap:100, crappy:100, christ:100, cum:10,
  // Curated DANGEROUS-tier compounds (their stems — ass/dick/damn — can't be substring-matched;
  // see STEM_ALLOW_MS). NOT here by product decision (2026-07-05): badass, kickass — compliments.
  dumbass:50, dumbasses:50, jackass:50, jackasses:50, smartass:50, hardass:50, asshat:50, asswipe:50,
  dickhead:10, dickheads:10, dammit:100, damnit:100, goddammit:100, goddamnit:100,
};

// ── Stem tier (restored from the iOS design — SAFE_WORDS/CURSE_STEMS were its orphaned
// remains; the web port had implemented exact matching only, which is how "horseshit"
// shipped unswapped AND unbleeped). SAFE-tier stems are substring-matched anywhere in a
// token: no common innocent English word contains them, so novel compounds/coinages
// (horseshit, batshit, fuckery, shitgibbon) are caught without a table entry. DANGEROUS
// stems (ass, hell, cock, dick, crap, cum, damn, christ) must NEVER join this table —
// passion/classic/hello/peacock/document/scrape all contain them; those families grow via
// curated exact entries above instead. Allowance = the stem's base value.
const STEM_ALLOW_MS: Record<string, number> = {
  fuck:0, shit:50, bitch:50, cunt:10, whore:10, slut:10, faggot:10, piss:50,
};
// Stand-in stems for soundalike-mode detection of swapped compounds (horseshizz, frickery).
// "arse" is deliberately absent — hearse/parse/coarse/rehearse would false-positive — so
// arse-family stand-ins need exact SOUNDALIKE_ALLOW_MS entries.
const SOUNDALIKE_STEM_ALLOW_MS: Record<string, number> = {
  frick:0, shizz:50, biatch:50,
};

// Clean sound-alike stand-ins for bleepMode="soundalike" (produced by swapSoundalikes before TTS).
// Kept in a SEPARATE table — NOT merged into ALLOW_FIRST_MS — and consulted ONLY when the roast's
// bleepMode is "soundalike" (see censorAllowance). This is deliberate: some stand-ins ("frick",
// "frickin", "arse") are words a comedic/PG-13 model can emit organically, so if they were global
// a default tone-mode roast (or an already-saved roast) containing them would suddenly bleep/mask —
// breaking byte-identical replay. Gating by bleepMode makes these fire ONLY on roasts that were
// actually generated with the soundalike swap. Allowance mirrors the SOURCE curse.
export const SOUNDALIKE_ALLOW_MS: Record<string, number> = {
  frick:0, fricked:0, fricking:0, fricker:0, fricks:0, frickin:0, frickwad:0, fricksake:0, frickface:0, frickhead:0,
  motherfricker:10, motherfrickers:10, motherfricking:10, clusterfrick:100, mindfrick:100,
  shizz:50, shizzshow:50, shizzing:50, shizzy:50, shizzhole:50, bullshizz:100, dipshizz:100,
  biatch:50, biatches:50,
  arse:10, arsehole:50, arseholes:0,
  dumbarse:50, dumbarses:50, jackarse:50, jackarses:50, smartarse:50, hardarse:50, arsehat:50, arsewipe:50,
};
export const SAFE_WORDS = new Set([
  "assassin","class","glass","grass","pass","passive","mass","massive","brass","embarrass","embarrassed",
  "compass","harass","bass","ambassador","hello","shell","michelle","dictionary","predict","cocktail",
  "peacock","scrap","scrape","assist","assistant","associate","assume","assess","asset","assay",
  // Safe-tier stem false positives (the stem layer substring-matches these otherwise):
  "shitake","shiitake","mishit",
]);
export const CURSE_STEMS = ["fuck","shit","ass","bitch","dick","damn","hell","piss","cunt","cock","pussy","whore","slut","bastard","christ","jesus","cum"];

/** The gate: null = clean/safe, else the ms onset allowance. Real curses are always detected;
 *  the clean sound-alike stand-ins are detected ONLY when bleepMode==="soundalike" (so they never
 *  bleep/mask an organic clean word in a default tone-mode roast). */
export function censorAllowance(raw: string, bleepMode: BleepMode = DEFAULT_BLEEP_MODE): number | null {
  const w = raw.toLowerCase().replace(/[^a-z0-9*]/g, "");
  if (!w) return null;
  if (SAFE_WORDS.has(w)) return null;
  if (w in ALLOW_FIRST_MS) return ALLOW_FIRST_MS[w];
  if (bleepMode === "soundalike" && w in SOUNDALIKE_ALLOW_MS) return SOUNDALIKE_ALLOW_MS[w];
  // Stem fallback: catch compounds/coinages the exact tables don't know ("horseshit").
  for (const stem in STEM_ALLOW_MS) if (w.includes(stem)) return STEM_ALLOW_MS[stem];
  if (bleepMode === "soundalike") {
    for (const stem in SOUNDALIKE_STEM_ALLOW_MS) if (w.includes(stem)) return SOUNDALIKE_STEM_ALLOW_MS[stem];
  }
  return null;
}

/** Is this caption word profane (and thus box-redactable)? Shares the single detector
 *  so the box mask engages on exactly the words the tone/mask/Rive-bar already censor. */
export const isRedacted = (raw: string, bleepMode: BleepMode = DEFAULT_BLEEP_MODE): boolean =>
  censorAllowance(raw, bleepMode) !== null;

// ── Sound-alike ("minced oath") swap for bleepMode="soundalike" ──────────────────
// A curated, DETERMINISTIC curse-form → clean-stand-in table. The swap runs BEFORE TTS
// (see runGeneration.ts / deckPipeline.ts) so the SPOKEN audio is clean — nothing offensive
// is ever actually said. Every stand-in is registered in SOUNDALIKE_ALLOW_MS above, so a
// soundalike-mode player still bleeps/boxes/ducks it exactly like the real word (full-bleep
// first; leak-proof). Coverage is the common savage curses (fuck/shit/bitch/ass families);
// curses absent from this table fall back to being spoken and bleeped as today. (The richer,
// in-context minced oaths come from the deferred B-prompt spice tier.)
export const SOUNDALIKE_MAP: Record<string, string> = {
  fuck: "frick", fucked: "fricked", fucking: "fricking", fucker: "fricker", fucks: "fricks",
  fuckin: "frickin", fuckwad: "frickwad", fucksake: "fricksake", fuckface: "frickface", fuckhead: "frickhead",
  "f*ck": "frick",
  motherfucker: "motherfricker", motherfuckers: "motherfrickers", motherfucking: "motherfricking",
  clusterfuck: "clusterfrick", mindfuck: "mindfrick",
  shit: "shizz", shitshow: "shizzshow", shits: "shizz", shitting: "shizzing", shitty: "shizzy",
  "sh*t": "shizz", shithole: "shizzhole", bullshit: "bullshizz", dipshit: "dipshizz",
  bitch: "biatch", bitches: "biatches", "b*tch": "biatch",
  ass: "arse", asshole: "arsehole", assholes: "arseholes",
  dumbass: "dumbarse", dumbasses: "dumbarses", jackass: "jackarse", jackasses: "jackarses",
  smartass: "smartarse", hardass: "hardarse", asshat: "arsehat", asswipe: "arsewipe",
};

// Stem transforms for compounds the exact map doesn't know: swap the curse stem in place
// (horseshit → horseshizz, fuckery → frickery). Only stems with an established stand-in;
// cunt/whore/slut/faggot/piss families have none — they stay spoken-but-bleeped (the stem
// DETECTION above still catches them) and surface via findUnhandledProfanity.
const STEM_SWAPS: ReadonlyArray<readonly [string, string]> = [
  ["fuck", "frick"], ["shit", "shizz"], ["bitch", "biatch"],
];

/** Re-apply a source token's capitalization pattern to its replacement (ALL CAPS → upper,
 *  Titlecase → capitalized, else lower). Keeps "Fuck"→"Frick", "FUCK"→"FRICK". */
function matchCase(src: string, repl: string): string {
  const hasLower = src !== src.toUpperCase();
  const hasUpper = src !== src.toLowerCase();
  if (hasUpper && !hasLower) return repl.toUpperCase();
  if (hasUpper && src[0] === src[0].toUpperCase() && src[0] !== src[0].toLowerCase()) {
    return repl[0].toUpperCase() + repl.slice(1);
  }
  return repl;
}

/** Replace every curse in a script with its clean sound-alike, preserving case and
 *  surrounding punctuation. Exact SOUNDALIKE_MAP forms first; then the STEM_SWAPS
 *  fallback rewrites the curse stem inside novel compounds (horseshit → horseshizz).
 *  SAFE_WORDS guard the stem path (shitake stays shitake); clean words and curses with
 *  no stand-in pass through untouched. */
export function swapSoundalikes(script: string): string {
  return script.replace(/[A-Za-z*]+/g, (token) => {
    const key = token.toLowerCase().replace(/[^a-z0-9*]/g, "");
    const repl = SOUNDALIKE_MAP[key];
    if (repl) return matchCase(token, repl);
    if (SAFE_WORDS.has(key)) return token;
    for (const [stem, standIn] of STEM_SWAPS) {
      if (key.includes(stem)) {
        return token.replace(new RegExp(stem, "gi"), (m) => matchCase(m, standIn));
      }
    }
    return token;
  });
}

/** The [censor] breadcrumb: tokens still carrying a REAL safe-tier curse stem — i.e. words
 *  the swap could not clean (no stand-in exists: cunt/whore/slut/piss families and any
 *  future gap). They ARE still bleeped via stem detection; this surfaces them so the
 *  tables/tiers grow from evidence instead of surprises. Run on the post-swap script. */
export function findUnhandledProfanity(script: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const token of script.match(/[A-Za-z*]+/g) ?? []) {
    const key = token.toLowerCase().replace(/[^a-z0-9*]/g, "");
    if (!key || seen.has(key) || SAFE_WORDS.has(key)) continue;
    for (const stem in STEM_ALLOW_MS) {
      if (key.includes(stem)) { out.push(token); seen.add(key); break; }
    }
  }
  return out;
}

/** Redact a caption word per the chosen mask. Clean words pass through untouched
 *  (this is why the default is safe). Default mask "vowels" == today's behavior.
 *  - "vowels"    → star every vowel        (shit → sh*t,  fuck → f*ck)
 *  - "keepFirst" → keep the first `keep` letters, star the rest (shit → sh**, fuck → fu**)
 *  - "box"       → still returns a redacted string so nothing leaks if the box render
 *                  is unavailable; the solid box is drawn on top by the caption renderers.
 *  - "none"      → no caption redaction at all — even profane words show in full (the audio
 *                  bleep is governed separately by bleepMode).
 */
export function redactWord(
  raw: string,
  mask: CaptionMask = DEFAULT_CAPTION_MASK,
  bleepMode: BleepMode = DEFAULT_BLEEP_MODE,
  keep = KEEP_FIRST_LETTERS,
): string {
  if (mask === "none") return raw; // uncensored captions — show the full word
  if (censorAllowance(raw, bleepMode) === null) return raw;
  if (mask === "keepFirst") {
    let i = 0;
    return raw.replace(/[a-z]/gi, (c) => (i++ < keep ? c : "*"));
  }
  return raw.replace(/[aeiou]/gi, "*"); // "vowels" (default) and the leak-safe base for "box"
}
